import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
} from '@mui/material';
import { Search as SearchIcon, FilterList as FilterIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { usersApi } from '../../../api/users.api';
import { useUIStore } from '../../../store';

dayjs.extend(relativeTime);

const NotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [notifications, setLocalNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [readFilter, setReadFilter] = useState('all'); // 'all', 'read', 'unread'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const { setNotifications, showError } = useUIStore();

  useEffect(() => {
    let isActive = true;

    const loadNotifications = async () => {
      setLoading(true);
      try {
        // Build query parameters
        const params = { limit: 500 };

        // Add search parameter
        if (searchTerm && searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        // Add read/unread filter
        if (readFilter === 'read') {
          params.isRead = 'true';
        } else if (readFilter === 'unread') {
          params.isRead = 'false';
        }

        // Add date filters
        if (dateFrom) {
          params.dateFrom = dateFrom;
        }
        if (dateTo) {
          params.dateTo = dateTo;
        }

        const response = await usersApi.getNotifications(params);
        const payload = response?.data?.data ?? response?.data ?? [];
        const list = Array.isArray(payload) ? payload : [];
        const mapped = list.map((item) => ({
          id: item.id,
          title: item.title || 'Notification',
          message: item.message || '',
          type: item.category || item.sender_type || 'notification',
          read: Number(item.is_read) === 1,
          createdAt: item.created_at || item.createdAt || item.sent_at || new Date().toISOString(),
        }));

        if (!isActive) return;
        setLocalNotifications(mapped);
        setNotifications(mapped);

        const unread = mapped.filter((notification) => !notification.read);
        if (unread.length > 0 && readFilter !== 'read') {
          try {
            await Promise.all(unread.map((notification) => usersApi.markNotificationRead(notification.id)));
            if (!isActive) return;
            const readAll = mapped.map((notification) => ({ ...notification, read: true }));
            setLocalNotifications(readAll);
            setNotifications(readAll);
          } catch (error) {
            console.error('Failed to mark notifications as read:', error);
            showError('Failed to mark notifications as read');
          }
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
        showError('Failed to load notifications');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadNotifications();
    return () => {
      isActive = false;
    };
  }, [setNotifications, showError, searchTerm, readFilter, dateFrom, dateTo]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setReadFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View all notifications sent to your account.
        </Typography>
      </Stack>

      {/* Filter Section */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <FilterIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                Filters
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {/* Search Input */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search in title or message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Read/Unread Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="read-filter-label">Status</InputLabel>
                  <Select
                    labelId="read-filter-label"
                    id="read-filter"
                    value={readFilter}
                    label="Status"
                    onChange={(e) => setReadFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="unread">Unread</MenuItem>
                    <MenuItem value="read">Read</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Date From */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="From Date"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Date To */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="To Date"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Clear Filters Button */}
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClearFilters}
                  disabled={!searchTerm && readFilter === 'all' && !dateFrom && !dateTo}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No notifications available.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              variant="outlined"
              sx={{
                borderColor: notification.read ? 'divider' : 'primary.light',
                bgcolor: notification.read ? 'background.paper' : 'action.hover',
              }}
            >
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {notification.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={notification.read ? 'Read' : 'Unread'}
                      color={notification.read ? 'default' : 'primary'}
                      variant={notification.read ? 'outlined' : 'filled'}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(notification.createdAt).fromNow()}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default NotificationsPage;
